/**
 * First-touch acquisition attribution (UTM + Google Click ID).
 * Captured on inbound landing, persisted in a first-party cookie, and copied
 * onto the User row when a brand-new account is created.
 */

export const ACQUISITION_COOKIE = "cl_acq";
export const ACQUISITION_COOKIE_MAX_AGE = 90 * 24 * 60 * 60;

export const ATTRIBUTION_QUERY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
] as const;

const NESTED_URL_KEYS = ["callbackUrl", "returnUrl", "redirect", "next"] as const;
const MAX_VALUE_LEN = 200;

export type AcquisitionAttribution = {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
};

export type UserAcquisitionFields = {
  acquisitionSource?: string;
  acquisitionMedium?: string;
  acquisitionCampaign?: string;
  acquisitionContent?: string;
  acquisitionTerm?: string;
  acquisitionGclid?: string;
  acquisitionCapturedAt?: Date;
};

export function emptyAttribution(): AcquisitionAttribution {
  return {
    source: null,
    medium: null,
    campaign: null,
    content: null,
    term: null,
    gclid: null,
    gbraid: null,
    wbraid: null,
  };
}

export function hasAttribution(attr: AcquisitionAttribution): boolean {
  return Boolean(
    attr.source ||
      attr.medium ||
      attr.campaign ||
      attr.content ||
      attr.term ||
      attr.gclid ||
      attr.gbraid ||
      attr.wbraid,
  );
}

function cleanValue(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim().slice(0, MAX_VALUE_LEN);
  if (!trimmed) return null;
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) return null;
  return trimmed;
}

function applySearchParams(target: AcquisitionAttribution, params: URLSearchParams): void {
  target.source = target.source || cleanValue(params.get("utm_source"));
  target.medium = target.medium || cleanValue(params.get("utm_medium"));
  target.campaign = target.campaign || cleanValue(params.get("utm_campaign"));
  target.content = target.content || cleanValue(params.get("utm_content"));
  target.term = target.term || cleanValue(params.get("utm_term"));
  target.gclid = target.gclid || cleanValue(params.get("gclid"));
  target.gbraid = target.gbraid || cleanValue(params.get("gbraid"));
  target.wbraid = target.wbraid || cleanValue(params.get("wbraid"));
}

function tryParseNestedUrl(raw: string | null): URLSearchParams | null {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("http://") || decoded.startsWith("https://")) {
      return new URL(decoded).searchParams;
    }
    if (decoded.startsWith("/")) {
      const q = decoded.includes("?") ? decoded.slice(decoded.indexOf("?") + 1) : "";
      return q ? new URLSearchParams(q) : null;
    }
    if (decoded.includes("=")) return new URLSearchParams(decoded);
  } catch {
    /* ignore malformed nested URLs */
  }
  return null;
}

/** Read UTMs / gclid from a query string, including values nested in callbackUrl. */
export function parseAttributionSearch(search: string | URLSearchParams): AcquisitionAttribution {
  const attr = emptyAttribution();
  const params = typeof search === "string" ? new URLSearchParams(search.startsWith("?") ? search.slice(1) : search) : search;
  applySearchParams(attr, params);
  for (const key of NESTED_URL_KEYS) {
    const nested = tryParseNestedUrl(params.get(key));
    if (nested) applySearchParams(attr, nested);
  }
  return attr;
}

export function attributionToQuery(attr: AcquisitionAttribution): Record<string, string> {
  const out: Record<string, string> = {};
  if (attr.source) out.utm_source = attr.source;
  if (attr.medium) out.utm_medium = attr.medium;
  if (attr.campaign) out.utm_campaign = attr.campaign;
  if (attr.content) out.utm_content = attr.content;
  if (attr.term) out.utm_term = attr.term;
  if (attr.gclid) out.gclid = attr.gclid;
  if (attr.gbraid) out.gbraid = attr.gbraid;
  if (attr.wbraid) out.wbraid = attr.wbraid;
  return out;
}

export function serializeAttributionCookie(attr: AcquisitionAttribution): string {
  const params = new URLSearchParams(attributionToQuery(attr));
  return params.toString();
}

export function parseAttributionCookie(value: string | undefined | null): AcquisitionAttribution {
  if (!value) return emptyAttribution();
  try {
    const decoded = decodeURIComponent(value);
    return parseAttributionSearch(decoded);
  } catch {
    return emptyAttribution();
  }
}

export function firstTouchMerge(
  existing: AcquisitionAttribution,
  incoming: AcquisitionAttribution,
): AcquisitionAttribution {
  return {
    source: existing.source || incoming.source,
    medium: existing.medium || incoming.medium,
    campaign: existing.campaign || incoming.campaign,
    content: existing.content || incoming.content,
    term: existing.term || incoming.term,
    gclid: existing.gclid || incoming.gclid,
    gbraid: existing.gbraid || incoming.gbraid,
    wbraid: existing.wbraid || incoming.wbraid,
  };
}

function readBodyString(obj: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "string") {
      const cleaned = cleanValue(value);
      if (cleaned) return cleaned;
    }
  }
  return null;
}

/** Accepts camelCase, snake_case, or utm_* keys from the register request body. */
export function sanitizeAttributionInput(input: unknown): AcquisitionAttribution {
  if (!input || typeof input !== "object") return emptyAttribution();
  const obj = input as Record<string, unknown>;
  const nested =
    obj.acquisition && typeof obj.acquisition === "object"
      ? (obj.acquisition as Record<string, unknown>)
      : obj;
  return {
    source: readBodyString(nested, ["source", "acquisitionSource", "acquisition_source", "utm_source"]),
    medium: readBodyString(nested, ["medium", "acquisitionMedium", "acquisition_medium", "utm_medium"]),
    campaign: readBodyString(nested, ["campaign", "acquisitionCampaign", "acquisition_campaign", "utm_campaign"]),
    content: readBodyString(nested, ["content", "acquisitionContent", "acquisition_content", "utm_content"]),
    term: readBodyString(nested, ["term", "acquisitionTerm", "acquisition_term", "utm_term"]),
    gclid: readBodyString(nested, ["gclid", "acquisitionGclid", "acquisition_gclid"]),
    gbraid: readBodyString(nested, ["gbraid"]),
    wbraid: readBodyString(nested, ["wbraid"]),
  };
}

export function attributionFromCookieHeader(cookieHeader: string | null | undefined): AcquisitionAttribution {
  if (!cookieHeader) return emptyAttribution();
  const match = cookieHeader.match(new RegExp(`(?:^|;\\s*)${ACQUISITION_COOKIE}=([^;]*)`));
  if (!match) return emptyAttribution();
  try {
    return parseAttributionCookie(decodeURIComponent(match[1]));
  } catch {
    return parseAttributionCookie(match[1]);
  }
}

export function resolveRequestAttribution(req: Request, bodyAcquisition?: unknown): AcquisitionAttribution {
  const fromCookie = attributionFromCookieHeader(req.headers.get("cookie"));
  const fromBody = sanitizeAttributionInput(bodyAcquisition);
  return firstTouchMerge(fromCookie, fromBody);
}

export function toUserAcquisitionFields(attr: AcquisitionAttribution): UserAcquisitionFields {
  if (!hasAttribution(attr)) return {};
  const fields: UserAcquisitionFields = { acquisitionCapturedAt: new Date() };
  if (attr.source) fields.acquisitionSource = attr.source;
  if (attr.medium) fields.acquisitionMedium = attr.medium;
  if (attr.campaign) fields.acquisitionCampaign = attr.campaign;
  if (attr.content) fields.acquisitionContent = attr.content;
  if (attr.term) fields.acquisitionTerm = attr.term;
  if (attr.gclid) fields.acquisitionGclid = attr.gclid;
  return fields;
}

export function attributionCookieOptions() {
  return {
    maxAge: ACQUISITION_COOKIE_MAX_AGE,
    path: "/",
    sameSite: "lax" as const,
    secure: process.env.NODE_ENV === "production",
  };
}

export function persistAcquisitionInBrowser(): AcquisitionAttribution {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return emptyAttribution();
  }
  const incoming = parseAttributionSearch(window.location.search);
  const existing = parseAttributionCookie(readBrowserCookie(ACQUISITION_COOKIE));
  const merged = firstTouchMerge(existing, incoming);
  if (hasAttribution(merged) && !hasAttribution(existing)) {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${ACQUISITION_COOKIE}=${encodeURIComponent(serializeAttributionCookie(merged))}; Max-Age=${ACQUISITION_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  }
  return merged;
}

export function getStoredAttribution(): AcquisitionAttribution {
  if (typeof window === "undefined") return emptyAttribution();
  const fromCookie = parseAttributionCookie(readBrowserCookie(ACQUISITION_COOKIE));
  const fromUrl = parseAttributionSearch(window.location.search);
  return firstTouchMerge(fromCookie, fromUrl);
}

function readBrowserCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? match[1] : null;
}
